import type { LLMStreamEvent } from '../../../contracts/harness.types';

const MAX_TOOL_NAMES_PER_TURN = 500;
const TOOL_WIRE_NAME = /^[a-zA-Z0-9_.-]{1,128}$/;
const INVALID_TOOL_NAME = 'other';

const analyticsToolName = (value: string): string =>
  TOOL_WIRE_NAME.test(value) ? value : INVALID_TOOL_NAME;

export type WorkTurnMetricsSnapshot = {
  duration_ms?: number;
  time_to_first_token_ms?: number;
  response_length?: number;
  tool_call_count?: number;
  tools_used?: string[];
  had_approval_request?: boolean;
  is_agentic_use?: boolean;
};

/**
 * Accumulates turn-level timing and stream counters for work_run_completed.
 * Only records values that can be measured from timestamps / stream events.
 */
export class WorkTurnMetricsTracker {
  private startedAtMs: number | null = null;
  private firstTokenAtMs: number | null = null;
  private responseLength = 0;
  private toolCallCount = 0;
  private toolNames = new Set<string>();
  private hadApprovalRequest = false;

  /** Call when the turn is submitted (or turn_started arrives). */
  markStarted(atMs: number = Date.now()): void {
    this.startedAtMs = atMs;
    this.firstTokenAtMs = null;
    this.responseLength = 0;
    this.toolCallCount = 0;
    this.toolNames.clear();
    this.hadApprovalRequest = false;
  }

  observe(event: LLMStreamEvent, atMs: number = Date.now()): void {
    if (this.startedAtMs == null) {
      if (event.type === 'turn_started') {
        this.markStarted(atMs);
      }
      return;
    }

    if (
      this.firstTokenAtMs == null &&
      event.type === 'text_delta' &&
      typeof event.text === 'string' &&
      event.text.length > 0
    ) {
      this.firstTokenAtMs = atMs;
    }

    if (event.type === 'text_delta' && typeof event.text === 'string') {
      this.responseLength += event.text.length;
    }

    if (event.type === 'tool_call_start') {
      this.toolCallCount += 1;
      if (event.tool_name) {
        const toolName = analyticsToolName(event.tool_name);
        if (
          this.toolNames.has(toolName) ||
          this.toolNames.size < MAX_TOOL_NAMES_PER_TURN
        ) {
          this.toolNames.add(toolName);
        }
      }
    }

    if (event.type === 'approval_requested') {
      this.hadApprovalRequest = true;
    }
  }

  snapshot(atMs: number = Date.now()): WorkTurnMetricsSnapshot {
    if (this.startedAtMs == null) return {};

    const duration_ms = Math.max(0, atMs - this.startedAtMs);
    const time_to_first_token_ms =
      this.firstTokenAtMs != null
        ? Math.max(0, this.firstTokenAtMs - this.startedAtMs)
        : undefined;

    return {
      duration_ms,
      ...(time_to_first_token_ms != null ? { time_to_first_token_ms } : {}),
      ...(this.responseLength > 0 ? { response_length: this.responseLength } : {}),
      ...(this.toolCallCount > 0 ? { tool_call_count: this.toolCallCount } : {}),
      tools_used: [...this.toolNames].sort(),
      had_approval_request: this.hadApprovalRequest,
      is_agentic_use: this.toolCallCount > 0,
    };
  }

  reset(): void {
    this.startedAtMs = null;
    this.firstTokenAtMs = null;
    this.responseLength = 0;
    this.toolCallCount = 0;
    this.toolNames.clear();
    this.hadApprovalRequest = false;
  }
}
